import { BadInputError } from '@watershed/errors/BadInputError';

import { ASTCall, stringifyExpr } from '@watershed/lscript/ast';
import { Context, Func, SqlExpression } from '../types';
import { ExprType, stringifyType } from '@watershed/lscript/expr_types';
import {
  assertKwargShape,
  typecheckExpression,
  TypecheckWarning,
} from '../typecheck';
import { evalExpression } from '../eval';
import { exprToSql } from '../sql';
import { safeMaxStage } from '../utils';
import { mergeErrors } from '../sql/internal';

// Function for logical or. Syntax:
// or(true, false, ...)
// returns true if any of its arguments are true
export const or: Func = {
  typecheck(
    ctx: Context,
    call: ASTCall,
    warnings?: Array<TypecheckWarning>
  ): ExprType {
    if (call.args.length < 2) {
      throw new BadInputError(
        '"or" function must be invoked with at least two arguments'
      );
    }
    assertKwargShape(call, [], false, undefined, undefined);

    const argTypes = call.args.map((arg) =>
      typecheckExpression(ctx, arg, warnings)
    );

    argTypes.forEach((a, i) => {
      if (a.ty !== 'boolean') {
        throw new BadInputError(
          `All arguments of "or" must be a boolean, got ${stringifyType(
            a
          )} at position ${i} (${stringifyExpr(call.args[i])})`
        );
      }
    });

    return { ty: 'boolean' };
  },

  eval(ctx: Context, call: ASTCall): unknown {
    const vals = call.args.map((arg) => evalExpression(ctx, arg));
    return vals.some((v) => v === true);
  },

  toSql(ctx: Context, call: ASTCall): SqlExpression {
    const argsSql = call.args.map((arg) => exprToSql(ctx, arg));
    return {
      expr: `(${argsSql.map((a) => a.expr).join(' OR ')})`,
      stage: safeMaxStage(argsSql),
      unitExpr: null,
      error: mergeErrors(argsSql.map((a) => a.error)),
      trace: null,
    };
  },
};
